import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantDiningRoomService from '../../services/restaurant/RestaurantDiningRoomService';
import RestaurantTableService from '../../services/restaurant/RestaurantTableService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeRestaurantTableModal,
  openRestaurantTableCreate,
  openRestaurantTableEdit,
  resetRestaurantTableResults,
  setRestaurantTableError,
  setRestaurantTableLoading,
  setRestaurantTablePage,
  setRestaurantTableSaving,
  setRestaurantTableUpdating,
  setTableDiningRooms,
  updateRestaurantTableField,
} from '../../store/slices/restaurant/restaurantTableSlice';
import {
  RestaurantTableDTO,
  RestaurantTableFilters,
  RestaurantTableForm,
  RestaurantTableStatus,
} from '../../types/restaurant/RestaurantTableTypes';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantTables = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantTable);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const requestId = useRef(0);
  const optionsRequestId = useRef(0);

  const loadOptions = async () => {
    if (!branchId) return;
    const currentRequest = ++optionsRequestId.current;
    try {
      const rooms = await RestaurantDiningRoomService.list(branchId, true);
      if (currentRequest !== optionsRequestId.current) return;
      dispatch(setTableDiningRooms(rooms.data ?? []));
    } catch (error) {
      handle(error);
    }
  };

  const load = async (filters: RestaurantTableFilters, silent = false) => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    dispatch(setRestaurantTableError(''));
    dispatch(
      silent
        ? setRestaurantTableUpdating(true)
        : setRestaurantTableLoading(true)
    );
    try {
      const page = await RestaurantTableService.listFiltered(branchId, filters);
      if (currentRequest !== requestId.current) return;
      dispatch(setRestaurantTablePage(page));
      return page;
    } catch (error: any) {
      if (currentRequest !== requestId.current) return;
      dispatch(
        setRestaurantTableError(
          error?.response?.data?.message || 'No fue posible cargar las mesas.'
        )
      );
    } finally {
      if (currentRequest === requestId.current) {
        dispatch(
          silent
            ? setRestaurantTableUpdating(false)
            : setRestaurantTableLoading(false)
        );
      }
    }
  };

  const edit = async (table: RestaurantTableDTO) => {
    try {
      const response = await RestaurantTableService.get(table.id);
      dispatch(openRestaurantTableEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const save = async (
    filters: RestaurantTableFilters,
    onEmptyPage: () => void
  ) => {
    if (!branchId || state.saving) return;
    dispatch(setRestaurantTableSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantTableService.update(state.form.id, state.form)
        : await RestaurantTableService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Mesa guardada',
        response.message || 'La mesa se guardó correctamente.'
      );
      dispatch(closeRestaurantTableModal());
      const page = await load(filters, true);
      if (page?.empty && filters.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setRestaurantTableSaving(false));
    }
  };

  const updateStatus = async (
    table: RestaurantTableDTO,
    status: RestaurantTableStatus,
    filters: RestaurantTableFilters,
    onEmptyPage: () => void
  ) => {
    if (table.status === status) return;
    try {
      const response = await RestaurantTableService.updateStatus(
        table.id,
        status
      );
      show(
        response.type || 'success',
        response.title || 'Estado actualizado',
        response.message || 'El estado de la mesa fue actualizado.'
      );
      const page = await load(filters, true);
      if (page?.empty && filters.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    }
  };

  const updateActive = (
    table: RestaurantTableDTO,
    active: boolean,
    filters: RestaurantTableFilters,
    onEmptyPage: () => void
  ) => {
    const execute = async () => {
      try {
        const response = await RestaurantTableService.updateActive(
          table.id,
          active
        );
        show(
          response.type || 'success',
          response.title || 'Mesa actualizada',
          response.message || 'La disponibilidad de la mesa fue actualizada.'
        );
        const page = await load(filters, true);
        if (page?.empty && filters.page > 0) onEmptyPage();
      } catch (error) {
        handle(error);
      }
    };
    if (active) return execute();
    confirm({
      title: 'Desactivar mesa',
      message: `¿Desea desactivar “${table.name}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  const updateField = <K extends keyof RestaurantTableForm>(
    field: K,
    value: RestaurantTableForm[K]
  ) => dispatch(updateRestaurantTableField({ field, value }));

  return {
    ...state,
    load,
    loadOptions,
    edit,
    save,
    updateStatus,
    updateActive,
    updateField,
    resetResults: () => {
      requestId.current += 1;
      optionsRequestId.current += 1;
      dispatch(resetRestaurantTableResults());
    },
    openCreate: () => dispatch(openRestaurantTableCreate()),
    closeModal: () => dispatch(closeRestaurantTableModal()),
  };
};

export default useRestaurantTables;
